import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from "react-bootstrap";
import TabellaLista from '../../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { sports } from "../../../../components/Util/enum";
import { useNavigate } from 'react-router-dom';
import { formattaPrezzo } from "../../../../components/Util/prezzi";

// Valori dei filtri a chip, come compaiono nella lista.
const STATO_OPTIONS = ['Bozza', 'Attivo', 'Non attivo', 'Misto'];

export const LessonsTable = () => {

	const navigate = useNavigate();

	const [data_json, setDataJson] = useState(null);
	const [sportFilter, setSportFilter] = useState('');
	// Chip a scelta multipla: all'inizio tutti accesi
	const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);

	// Gli sport sono quelli delle lezioni caricate: di solito pochi per struttura.
	const sportOptions = useMemo(
		() => [...new Set((data_json || []).map((row) => row.sportType).filter(Boolean))].sort(),
		[data_json],
	);

	const filteredData = useMemo(
		() =>
			data_json &&
			data_json.filter(
				(row) =>
					(!sportFilter || row.sportType === sportFilter) &&
					filtroMultiplo(statoFilter, STATO_OPTIONS, row.status_label?.label),
			),
		[data_json, sportFilter, statoFilter],
	);

	const [loadingShowed, setLoadingShowed] = useState(false);
	
	// Colonna "Prezzo soci/tesserati": visibile solo per ASD/SSD (matrice regole clubNature)
	let clubNatureFromSession = null;
	try { clubNatureFromSession = JSON.parse(sessionStorage.getItem('userDetails'))?.clubNature; } catch (e) { clubNatureFromSession = null; }
	const showSociTesseratiColumn = clubNatureFromSession === 'ASD' || clubNatureFromSession === 'SSD';

	const data_column = [
		{
			Header: 'Nome',
			Footer: 'Nome',
			accessor: 'name',
		},
		{
			Header: 'Sport',
			Footer: 'Sport',
			accessor: 'sportType',
			disableGlobalFilter: true,
		},
		{
			Header: 'Staff Tecnico',
			Footer: 'Staff Tecnico',
			accessor: 'coachName',
			Cell: ({ value }) => value || '-',
		},
		{
			Header: 'Prezzo standard',
			Footer: 'Prezzo standard',
			accessor: 'standard_price',
			disableGlobalFilter: true,
		},
		...(showSociTesseratiColumn ? [{
			Header: 'Prezzo soci/tesserati',
			Footer: 'Prezzo soci/tesserati',
			accessor: 'federated_price',
			disableGlobalFilter: true,
		}] : []),
		{
			Header: 'N. Vendite',
			Footer: 'N. Vendite',
			accessor: 'purchasesCount',
			disableGlobalFilter: true,
			Cell: ({ value }) => {
				return value || 0;
			},
			disableFilters: true,
		},
		{
			Header: 'Stato',
			Footer: 'Stato',
			accessor: 'status_label',
			disableGlobalFilter: true,
			Cell: ({ value }) => {
				return (
					<div className="d-flex align-items-center">
						<i className={`fa fa-circle me-1 ${value?value.class:''}`}></i>
						{value?value.label:''}
					</div>
				)
			},
			disableFilters: true,
		},
	];

    function openDetail(row) {
        const targetId = Array.isArray(row.lessonIds) && row.lessonIds.length > 0 ? row.lessonIds[0] : row.id;
        const idsQuery = Array.isArray(row.lessonIds) && row.lessonIds.length > 1 ? `?ids=${row.lessonIds.join(',')}` : '';
        const isActiveOrCancelled = row.status === 'PURCHASABLE' || row.status === 'CANCELLED';
        const basePath = isActiveOrCancelled ? '/struttura/booking/lessonsview/' : '/struttura/booking/lessonseditmultiple/';
        navigate(basePath + targetId + idsQuery);
    }

	useEffect(() => {
		const fetchData = async () => {
			try {
				const tokenDetailsString = sessionStorage.getItem('userDetails');
				if (tokenDetailsString) {
					const tokenDetails = JSON.parse(tokenDetailsString);
					let token = tokenDetails.idToken;
					const apiClient = new MyApiClient();

					setLoadingShowed(true);
					const [bookingLessons] = await Promise.all([
						apiClient.getBookingLessons(token)
					]);
					setLoadingShowed(false);

					if(bookingLessons) {
						// Raggruppa lezioni che differiscono solo per staff tecnico
						const groups = {};
						bookingLessons.forEach(element => {
							// Chiave di raggruppamento basata su campi invarianti (escluso coach)
							const groupKey = [
								element.clubId || '',
								element.name || '',
								element.sportType || '',
								element.type || 'CLOSED',
								element.venueId || '',
								element.standard_price || 0,
								element.federated_price || 0,
							].join('|');

							// Risolvi label sport
							let sportLabel = '';
							sports.forEach(s => {
								if(s.value === element.sportType) sportLabel = s.label;
							});

                            if(!groups[groupKey]) {
                                groups[groupKey] = {
                                    name: element.name,
                                    sportLabel,
                                    standard_price: element.standard_price,
                                    federated_price: element.federated_price,
                                    lessonIds: [],
                                    groupIds: new Set(),
                                    idsDraft: [],
                                    idsPurchasable: [],
                                    idsCancelled: [],
                                    coachNames: new Set(),
                                    purchasesCount: 0,
                                    statuses: new Set(),
                                };
                            }

                            groups[groupKey].lessonIds.push(element.id);
                            if (element.groupId) {
                                groups[groupKey].groupIds.add(String(element.groupId));
                            }
                            groups[groupKey].coachNames.add(element.coachName || '');
                            groups[groupKey].purchasesCount += (element.purchasesCount || 0);
                            groups[groupKey].statuses.add(element.status);

							if(element.status === 'DRAFT') groups[groupKey].idsDraft.push(element.id);
							if(element.status === 'PURCHASABLE') groups[groupKey].idsPurchasable.push(element.id);
							if(element.status === 'CANCELLED') groups[groupKey].idsCancelled.push(element.id);
						});

                        const data = Object.values(groups).map(g => {
                            const statuses = Array.from(g.statuses);
                            const statusValue = statuses.length === 1 ? statuses[0] : 'MIXED';
                            let statusLabel = null;
                            if(statusValue === 'DRAFT') {
                                statusLabel = { label: 'Bozza', class: 'text-warning' };
							} else if(statusValue === 'PURCHASABLE') {
								statusLabel = { label: 'Attivo', class: 'text-success' };
							} else if(statusValue === 'CANCELLED') {
								statusLabel = { label: 'Non attivo', class: 'text-primary' };
							} else {
								statusLabel = { label: 'Misto', class: 'text-info' };
							}

							const coachName = Array.from(g.coachNames).filter(Boolean).join(', ');

                            const groupIdsArr = Array.from(g.groupIds);
                            return {
                                id: g.lessonIds[0],
                                name: g.name,
                                sportType: g.sportLabel,
                                coachName,
                                standard_price: formattaPrezzo(g.standard_price, { conZero: false }),
                                federated_price: formattaPrezzo(g.federated_price, { conZero: false }),
                                status: statusValue,
                                status_label: statusLabel,
                                purchasesCount: g.purchasesCount,
                                lessonIds: g.lessonIds,
                                groupIds: groupIdsArr,
                                groupId: groupIdsArr.length > 0 ? groupIdsArr[0] : null,
                                idsDraft: g.idsDraft,
                                idsPurchasable: g.idsPurchasable,
                                idsCancelled: g.idsCancelled,
                                hasDraft: g.idsDraft.length > 0,
                                hasPurchasable: g.idsPurchasable.length > 0,
                                hasCancelled: g.idsCancelled.length > 0,
                            };
                        });

						setDataJson(data);
					}
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		};

		fetchData();

	}, []);

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
				<Spinner animation="border" role="status">
					<span className="sr-only">Loading...</span>
				</Spinner>
				<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<TabellaLista
				pageList={true}
				cardTitle={'Lezioni'}
				hasSearch={true}
				searchPlaceholder={'Cerca per nome o staff tecnico'}
				hasFilter={false}
				filterControl={
					<div className="sid-chip-filters">
						{sportOptions.length > 1 && <ChipFilter label="Sport" options={sportOptions} value={sportFilter} onChange={setSportFilter} asDropdown />}
						<ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
					</div>
				}
				data_column={data_column}
				data_json={filteredData}
				openColumn={'name'}
				openRow={(row) => openDetail(row)}
				hasAddMore={true}
				linkAddMore={'/struttura/booking/lessons/new'}
				labelAddMore={'Aggiungi'}
				sort_by={'name'}
				sort_type={'asc'}
				modalCallBack={() => setModalCentered(true)}
			/>
		</>
	)
}
export default LessonsTable;